import moment from "moment";
import React, { useEffect, useState } from "react";
import MainDatatable from "../../components/datatable/MainDatatable.jsx";
import DatatableHeading from "../../components/datatable/DatatableHeading.jsx";
import { DeepSearchSpace } from "../../utils/common-function/index.js";
import { api_urls } from "../../utils/api-urls/index.js";

const AppErrors = () => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [filter, setFilter] = useState('all');

    const filteredData = DeepSearchSpace(
        filter === 'all' ? errors : errors.filter(e => e.resolved === (filter === 'resolved')),
        searchText
    );

    const columns = [
        { name: 'S.No.', selector: (row, index) => index + 1, width: '70px' },
        { name: 'Date', selector: row => row?.createdAt ? moment(row?.createdAt).format('DD MMM YYYY HH:mm') : 'N/A', width: '160px' },
        { name: 'Type', selector: row => row?.errorType || 'N/A', width: '120px' },
        { name: 'Number', selector: row => row?.number || 'N/A', width: '120px' },
        { name: 'Amount', selector: row => row?.amount ? '₹' + row.amount : 'N/A', width: '100px' },
        { name: 'Provider', selector: row => row?.provider || 'N/A', width: '100px' },
        { name: 'Error Code', selector: row => row?.errorCode || 'N/A', width: '120px' },
        { name: 'Error Message', selector: row => row?.errorMessage || row?.message || 'N/A', width: '250px', wrap: true },
        { name: 'Status', selector: row => row?.resolved ? 'Resolved' : 'Pending', width: '100px',
            cell: row => (
                <span style={{ 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    backgroundColor: row?.resolved ? '#e8f5e9' : '#ffebee',
                    color: row?.resolved ? '#388e3c' : '#d32f2f',
                    fontSize: '12px'
                }}>
                    {row?.resolved ? 'Resolved' : 'Pending'}
                </span>
            )
        },
        { name: 'Action', width: '100px',
            cell: row => (
                <button 
                    onClick={() => handleResolve(row._id)}
                    disabled={row?.resolved}
                    style={{ 
                        padding: '5px 10px', 
                        borderRadius: '4px', 
                        border: 'none',
                        backgroundColor: row?.resolved ? '#ccc' : '#1976d2',
                        color: '#fff',
                        cursor: row?.resolved ? 'not-allowed' : 'pointer',
                        fontSize: '12px'
                    }}
                >
                    {row?.resolved ? 'Done' : 'Resolve'}
                </button>
            )
        },
    ];

    const fetchErrors = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${api_urls}api/recharge/errors`);
            const data = await response.json();
            if (data?.success) {
                setErrors(data?.errors || []);
            }
        } catch (error) {
            console.error('Error fetching errors:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleResolve = async (errorId) => {
        try {
            const response = await fetch(`${api_urls}api/recharge/resolve-error`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ errorId })
            });
            const data = await response.json();
            if (data?.success) {
                fetchErrors();
            }
        } catch (error) {
            console.error('Error resolving:', error);
        }
    };

    useEffect(() => {
        fetchErrors();
    }, []);

    const pendingCount = errors.filter(e => !e.resolved).length;
    const resolvedCount = errors.filter(e => e.resolved).length;

    return (
        <>
            <div style={{ padding: "20px", backgroundColor: "#fff", marginBottom: "20px", boxShadow: '0px 0px 5px lightgrey', borderRadius: "10px" }}>
                <DatatableHeading title="App Errors & Failed Recharges" data={errors} />

                {/* Summary Cards */}
                <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
                    <div style={{ padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px', minWidth: '150px' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>Total Errors</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>{errors.length}</div>
                    </div>
                    <div style={{ padding: '15px', backgroundColor: '#ffebee', borderRadius: '8px', minWidth: '150px' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>Pending</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d32f2f' }}>{pendingCount}</div>
                    </div>
                    <div style={{ padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '8px', minWidth: '150px' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>Resolved</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#388e3c' }}>{resolvedCount}</div>
                    </div>
                </div>

                {/* Filters */}
                <div style={{ display: "flex", justifyContent: "space-between", gap: "20px", alignItems: 'center', marginBottom: "20px" }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                            onClick={() => setFilter('all')}
                            style={{ 
                                padding: '8px 16px', 
                                borderRadius: '5px', 
                                border: filter === 'all' ? '2px solid #1976d2' : '1px solid #ccc',
                                backgroundColor: filter === 'all' ? '#e3f2fd' : '#fff',
                                cursor: 'pointer'
                            }}
                        >
                            All ({errors.length})
                        </button>
                        <button 
                            onClick={() => setFilter('pending')}
                            style={{ 
                                padding: '8px 16px', 
                                borderRadius: '5px', 
                                border: filter === 'pending' ? '2px solid #d32f2f' : '1px solid #ccc',
                                backgroundColor: filter === 'pending' ? '#ffebee' : '#fff',
                                cursor: 'pointer'
                            }}
                        >
                            Pending ({pendingCount})
                        </button>
                        <button 
                            onClick={() => setFilter('resolved')}
                            style={{ 
                                padding: '8px 16px', 
                                borderRadius: '5px', 
                                border: filter === 'resolved' ? '2px solid #388e3c' : '1px solid #ccc',
                                backgroundColor: filter === 'resolved' ? '#e8f5e9' : '#fff',
                                cursor: 'pointer'
                            }}
                        >
                            Resolved ({resolvedCount})
                        </button>
                        <button 
                            onClick={fetchErrors}
                            style={{ 
                                padding: '8px 16px', 
                                borderRadius: '5px', 
                                border: '1px solid #1976d2',
                                backgroundColor: '#1976d2',
                                color: '#fff',
                                cursor: 'pointer'
                            }}
                        >
                            Refresh
                        </button>
                    </div>
                    <input 
                        type='search' 
                        value={searchText} 
                        onChange={(e) => setSearchText(e.target.value)} 
                        placeholder='Search...' 
                        style={{ padding: '5px 10px', borderRadius: '5px', border: '1px solid #ccc', width: '100%', maxWidth: '250px', fontSize: '15px' }} 
                    />
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
                ) : (
                    <MainDatatable columns={columns} data={filteredData} />
                )}
            </div>
        </>
    );
};

export default AppErrors;
